import * as React from "react";
import { EAIClient } from "Services/EAIClient";
import DatePicker from "react-datepicker";
import {Button, Col, Form, Row, OverlayTrigger, Tooltip, Dropdown, InputGroup} from "react-bootstrap";
import { EnfluxTerm } from "Stores/superStore/ingestion";
import { Utils } from "@/common/utils";
import {ColumnDef} from "@tanstack/react-table";
import {CrudModal} from "Components/Common/CrudModal";
import {useEffect, useState} from "react";
import {ActionWrapper} from "Components/Common/ActionWrapper";
import {isDatetimeStringFormatSorter} from "@/common/sorters";
import {useTerms} from "@/hooks/terms";
import {useAppContext} from "@/contexts/app";
import {useFeatureGating} from "@/hooks/feature-gating";
import { StyledTable } from "Components/Common/StyledTable";
import DebouncedInput from "Components/Common/DebouncedInput";
import { MagnifyingIcon } from "Components/Icons/MagnifyingIcon";
import { CalendarIcon } from "Components/Icons/CalendarIcon";
import CustomToggle from "Components/Common/CustomToggle";
import PageTitle from "Components/Common/PageTitle";


export default function EnfluxTermsListPage() {
    const [showModal, setShowModal] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState<EnfluxTerm | undefined>(undefined);
    const [termName, setTermName] = useState("");
    const [formTermName, setFormTermName] = useState("");
    const [formTermType, setFormTermType] = useState("");
    const [formTermDatesVerified, setFormTermDatesVerified] = useState<boolean>(false);
    const [formTermCalendarYear, setFormTermCalendarYear] = useState<string | null>(null);
    const [formTermAcadYearStart, setFormTermAcadYearStart] = useState<string | null>(null);
    const [formTermAcadYearEnd, setFormTermAcadYearEnd] = useState<string | null>(null);
    const [formTermOrder, setFormTermOrder] = useState(0);
    const [formTermStartDate, setFormTermStartDate] = useState<Date | undefined>(undefined);
    const [formTermEndDate, setFormTermEndDate] = useState<Date | undefined>(undefined);
    const [showFilters, setShowFilters] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const yearsOptions = [...Array(new Date().getFullYear() - 2004)].map((v, i) => 2010 + i);
    const [typeFilter, setTypeFilter] = useState("");
    const [acadYearFilter, setAcadYearFilter] = useState<number | null>(null);
    const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
    const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
    const {dispatchError} = useAppContext();
    const client = new EAIClient();

    const [isUpdatingTerms, setIsUpdatingTerms] = useState(false);
    const {data, mutate} = useTerms();
    const {data: featureGatingData} = useFeatureGating();

    const typesAvailable = React.useMemo(() => {
        if (!data) return [];
        return Array.from(new Set(data.data.map((term) => term.term_type))).filter(
            Boolean
        );
    }, [data]);

    const filteredData = React.useMemo(() => {
        if (!data) return [];
        return data.data.filter((term) => {
            const searchLower = searchValue.toLowerCase();
            const matchesSearch =
                term.name.toLowerCase().includes(searchLower) ||
                (term.term && term.term.toLowerCase().includes(searchLower));

            const matchesType = typeFilter ? term.term_type === typeFilter : true;

            const matchesAcadYear = acadYearFilter
                ? Utils.isYearInAcadYear(acadYearFilter, term.acad_year)
                : true;

            const matchesStartDate = startDateFilter
                ? new Date(term.start_date) >= startDateFilter
                : true;
            const matchesEndDate = endDateFilter
                ? new Date(term.end_date) <= endDateFilter
                : true;

            return (
                matchesSearch &&
                matchesType &&
                matchesAcadYear &&
                matchesStartDate &&
                matchesEndDate
            );
        });
    }, [
        data,
        searchValue,
        typeFilter,
        acadYearFilter,
        startDateFilter,
        endDateFilter,
    ]);

    const columns: ColumnDef<EnfluxTerm>[] = [{
        header: "Name",
        accessorKey: "name",
    }, {
        header: "Term",
        accessorKey: "term",
    }, {
        header: "Type",
        accessorKey: "term_type",
    }, {
        header: "Calendar Year",
        accessorKey: "calendar_year",
    }, {
        header: "Order",
        accessorKey: "term_order",
    }, {
        header: "ACAD Year",
        accessorKey: "acad_year",
    }, {
        header: "Start Date",
        id: "start_date",
        accessorFn: (term: EnfluxTerm) => { return new Date(term.start_date).toISOString().slice(0, 10); },
        sortingFn: isDatetimeStringFormatSorter<EnfluxTerm>
    }, {
        header: "End Date",
        id: "end_date",
        accessorFn: (term: EnfluxTerm) => { return new Date(term.end_date).toISOString().slice(0, 10); },
        sortingFn: isDatetimeStringFormatSorter<EnfluxTerm>
    }];

    const handleAddClick = () => {
        setShowModal(true);
        setFormTermDatesVerified(true);
        setSelectedTerm(undefined);
        setTermName("");
        setFormTermName("");
        setFormTermType("");
        setFormTermCalendarYear(null);
        setFormTermAcadYearStart(null);
        setFormTermAcadYearEnd(null);
        setFormTermOrder(0);
        setFormTermStartDate(undefined);
        setFormTermEndDate(undefined);
    };

    const handleEditClick = (term: EnfluxTerm) => {
        const acadYears = term.acad_year.split("-");
        const calendarYear = yearsOptions.find(y => y === Number(term.calendar_year));
        setSelectedTerm(term);
        setShowModal(true);
        setTermName(term.name);
        setFormTermName(term.name);
        setFormTermType(term.term_type);
        setFormTermCalendarYear(calendarYear ? calendarYear.toString() : null);
        setFormTermAcadYearStart(acadYears.length === 2 ? acadYears[0] : null);
        setFormTermAcadYearEnd(acadYears.length === 2 ? acadYears[1] : null);
        setFormTermOrder(term.term_order);
        setFormTermStartDate(new Date(term.start_date));
        setFormTermEndDate(new Date(term.end_date));
        setFormTermDatesVerified(term.dates_verified);
    };

    const handleDeleteClick = async (term: EnfluxTerm) => {
        setIsUpdatingTerms(true);
        try {
            // delete term
            await client.deleteEnfluxTerm(term.id);
            mutate();
        } catch (e) {
            // handle error
            dispatchError(e);
        } finally {
            setIsUpdatingTerms(false);
        }
    };

    const saveTerm = async () => {
        if(!isValid()) return;
        setIsUpdatingTerms(true);

        const toSend: Partial<EnfluxTerm> = {
            name: formTermName,
            term_type: formTermType,
            calendar_year: formTermCalendarYear || "",
            acad_year: `${formTermAcadYearStart}-${formTermAcadYearEnd}`,
            term_order: formTermOrder,
            start_date: Utils.changeDatePickerTimeStampToEOD(formTermStartDate!),
            end_date: Utils.changeDatePickerTimeStampToEOD(formTermEndDate!),
            dates_verified: formTermDatesVerified,
        };

        try {
            if (selectedTerm) {
                await client.updateEnfluxTerm(selectedTerm.id, toSend);
            } else {
                await client.createEnfluxTerm(toSend);
            }
            mutate();
            handleModalClose();
        } catch (e) {
            // handle error
            dispatchError(e);
        } finally {
            setIsUpdatingTerms(false);
        }
    };

    const handleModalClose = () => {
        setSelectedTerm(undefined);
        setShowModal(false);
        setFormTermName("");
        setFormTermType("");
        setFormTermCalendarYear(null);
        setFormTermAcadYearStart(null);
        setFormTermAcadYearEnd(null);
        setFormTermOrder(0);
        setFormTermStartDate(undefined);
        setFormTermEndDate(undefined);
    };

    const handleTermNameChange = (ev: any) => {
        setFormTermName(ev.target.value);
    };

    const handleTermTypeChange = (ev: any) => {
        setTermOrder(formTermCalendarYear, ev.target.value);
        setFormTermType(ev.target.value);
    };

    const setTermOrder = (calendarYear: string | null, termType: string) => {
        if (!calendarYear) {
            return;
        }
        let defaultTermOrder = calendarYear.slice(-2);
        if (termType.includes("Winter")) {
            defaultTermOrder += "1";
        } else if (termType.includes("Spring")) {
            defaultTermOrder += "2";
        } else if (termType.includes("Summer")) {
            defaultTermOrder += "3";
        } else if (termType.includes("Fall")) {
            defaultTermOrder += "4";
        } else if (termType.includes("Academic Year")) {
            defaultTermOrder += "5";
        } else {
            defaultTermOrder += "6";
        }
        setFormTermOrder(Number(defaultTermOrder));
    };

    const handleTermCalendarYearChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCalendarYear = Number(ev.target.value);
        const defaultStartDate = new Date(selectedCalendarYear, 0);
        const defaultEndDate = new Date(selectedCalendarYear, 0, 2);
        setTermOrder(ev.target.value, formTermType);
        setFormTermCalendarYear(ev.target.value);
        setFormTermStartDate(defaultStartDate);
        setFormTermEndDate(defaultEndDate);
    };

    const handleTermAcadYearStartChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        const newTarget = ev.target.value.length > 0 ? ev.target.value : null;
        if (newTarget && formTermAcadYearEnd) {
            if (newTarget > formTermAcadYearEnd) {
                setFormTermAcadYearStart(null);
                return;
            }
        }
        setFormTermAcadYearStart(newTarget);
    };

    const handleTermAcadYearEndChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        const newTarget = ev.target.value.length > 0 ? ev.target.value : null;
        if (newTarget && formTermAcadYearStart) {
            if (newTarget < formTermAcadYearStart) {
                setFormTermAcadYearEnd(null);
                return;
            }
        }
        setFormTermAcadYearEnd(newTarget);
    };

    const handleTermOrderChange = (ev: any) => {
        setFormTermOrder(ev.target.value);
    };

    const handleTermStartDateChange = (d: Date) => {
        if(d) {
            setFormTermStartDate(d);
        }
    };

    const handleTermEndDateChange = (d: Date) => {
        if (d) {
            setFormTermEndDate(d);
        }
    };

    const isTermNameInUse = () => {
        if (termName === formTermName) return false;
        const found = data?.data.find(term => term.name === formTermName);
        return !!found;
    };

    const isValid = () => (
        formTermName &&
        formTermType &&
        formTermCalendarYear &&
        formTermAcadYearStart &&
        formTermAcadYearEnd &&
        (formTermOrder === 0 || formTermOrder) &&
        formTermStartDate &&
        formTermEndDate &&
        (new Date(formTermStartDate) < new Date(formTermEndDate))
    );

    useEffect(() => {
        Utils.setViewName("EnfluxTerms", "Enflux Terms");
    }, []);

    if (!featureGatingData || !Utils.isFeatureActiveSWC("ingestible", "enflux_terms", Array.isArray(featureGatingData?.data) ? featureGatingData.data : [])) {
        return <div className="text-center">Feature not enabled</div>;
    }

    if (Utils.hasKeyAdmin() || Utils.isSuperAdmin()) {
        columns.push({
            header: "Actions",
            cell: (cell) => renderActionButton(cell.row.original)
        });
    }

    const renderActionButtons = () => (
        <Col className="create-button">
            {(Utils.hasKeyAdmin() || Utils.isSuperAdmin()) && <Button onClick={handleAddClick} id="terms-create-term-button">create term</Button>}
        </Col>);

    const renderModalContent = () => (
        <>
            <Row>
                <Col xs={9}>
                    <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control onChange={ handleTermNameChange } value={ formTermName } placeholder="Enter a term" isInvalid={isTermNameInUse()}/>
                        <Form.Control.Feedback type="invalid">Name is already in use</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col xs={2}>
                    <Form.Group>
                        <Form.Label>Dates Verified</Form.Label>
                        <Form.Check
                            type="checkbox"
                            id="dates-verified-checkbox"
                            checked={formTermDatesVerified}
                            onChange={(ev) => setFormTermDatesVerified(ev.target.checked)}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Term Type</Form.Label>
                        <Form.Control onChange={ handleTermTypeChange } value={ formTermType } placeholder="e.g., Fall, Summer, Winter, Year"/>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Calendar Year</Form.Label>
                        <Form.Select onChange={handleTermCalendarYearChange} value={formTermCalendarYear || ""}>
                            <option value="">--Select Calendar Year--</option>
                            {yearsOptions.map(option => (<option value={option} key={option}>{option}</option>))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Academic Year Start</Form.Label>
                        <Form.Select onChange={handleTermAcadYearStartChange} value={formTermAcadYearStart || ""}>
                            <option value="">--Select Academic Year Start--</option>
                            {yearsOptions.map(option => (<option value={option} key={option}>{option}</option>))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Academic Year End</Form.Label>
                        <Form.Select onChange={handleTermAcadYearEndChange} value={formTermAcadYearEnd || ""}>
                            <option value="">--Select Academic Year End--</option>
                            {yearsOptions.map(option => (<option value={option} key={option}>{option}</option>))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>
                            Order <OverlayTrigger
                            placement="right"
                            overlay={<Tooltip>Order is used in dashboards to sort the terms. The default formula is Calendar Year and a number representing the term (1 for Winter, 2 for Spring, 3 for Summer, 4 for Fall, 5 for Academic Year, and 6 for Anything Else). For example, 2023 Fall would be 234.</Tooltip>}>
                            <i
                                className="icon icon-info-circle icon-light"
                                aria-hidden="true"/>
                        </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="number"
                                      onChange={ handleTermOrderChange }
                                      value={ formTermOrder }/>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <div>
                            <DatePicker
                                placeholderText="Select a date..."
                                selected={formTermStartDate ? new Date(formTermStartDate) : undefined}
                                onChange={handleTermStartDateChange}
                                className={"form-control"}
                            />
                        </div>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <div>
                            <DatePicker
                                placeholderText="Select a date..."
                                selected={formTermEndDate ? new Date(formTermEndDate) : undefined}
                                onChange={handleTermEndDateChange}
                                className={"form-control"}
                            />
                        </div>
                    </Form.Group>
                </Col>
            </Row>
        </>);

    const renderActionButton = (data: EnfluxTerm) => {
        return (
            <div className="action-button-group">
                <ActionWrapper
                    data={data}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                />
            </div>
        );
    };

    return (
        <div className="card-layout">
            <PageTitle pageTitle="Enflux Terms" />
            <Row>
                <Col md={12} lg={6} className="title">
                    <h3 id="reportsTitle">Enflux Terms</h3>
                </Col>
                {renderActionButtons()}
            </Row>
            <div className="search-container">
                <div className="search-bar">
                    <DebouncedInput
                        type="text"
                        value={searchValue}
                        onChange={(value) => setSearchValue(value as string)}
                        placeholder="Search by Name or Group"
                        className="search-input"
                    />
                    <MagnifyingIcon />
                </div>
                <Button
                    id="enflux-terms-filter-button"
                    disabled={false}
                    title="Filter"
                    className={"btn-icon"}
                    onClick={() => {
                        setShowFilters(!showFilters);
                    }}
                >
                    <i className="icon icon-filter"/>
                </Button>
            </div>
            {showFilters && (
                <AdvancedFilters
                    typesAvailable={typesAvailable}
                    typeFilter={typeFilter}
                    setTypeFilter={setTypeFilter}
                    acadYearFilter={acadYearFilter}
                    setAcadYearFilter={setAcadYearFilter}
                    startDateFilter={startDateFilter}
                    setStartDateFilter={setStartDateFilter}
                    endDateFilter={endDateFilter}
                    setEndDateFilter={setEndDateFilter}
                />
            )}
            <div className="list">
                <StyledTable
                    defaultData={filteredData || []}
                    columns={columns}
                    className="enflux-terms-table"
                    autoResetPageIndex={false}
                />
            </div>
            {showModal
                ? <CrudModal
                    typeLabel={"Term"}
                    isEdit={!!selectedTerm}
                    renderForm={renderModalContent}
                    onClose={handleModalClose}
                    onSave={saveTerm}
                    isDisabled={!isValid() || isUpdatingTerms}
                    scrollable={false}
                    showSpinner={isUpdatingTerms} /> : null}
        </div>
    );
}

interface AdvancedFiltersProps {
    typesAvailable: string[];
    typeFilter: string;
    setTypeFilter: React.Dispatch<React.SetStateAction<string>>;
    acadYearFilter: number | null;
    setAcadYearFilter: React.Dispatch<React.SetStateAction<number | null>>;
    startDateFilter: Date | null;
    setStartDateFilter: React.Dispatch<React.SetStateAction<Date | null>>;
    endDateFilter: Date | null;
    setEndDateFilter: React.Dispatch<React.SetStateAction<Date | null>>;
}

function AdvancedFilters({
    typesAvailable,
    typeFilter,
    setTypeFilter,
    acadYearFilter,
    setAcadYearFilter,
    startDateFilter,
    setStartDateFilter,
    endDateFilter,
    setEndDateFilter,
}: AdvancedFiltersProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    return (
        <div className="filter-container">
            <span>Filters</span>
            <div className="filter-input-container">
                <Dropdown
                    className="filter-dropdown"
                    onToggle={
                        (nextShow) => setIsDropdownOpen(nextShow)
                    }
                    show={isDropdownOpen}
                >
                    <Dropdown.Toggle
                        id="type-dropdown"
                        as={CustomToggle}
                        isDropdownOpen={isDropdownOpen}
                    >
                        {typeFilter || "Type"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setTypeFilter("")}>
                            All
                        </Dropdown.Item>
                        {typesAvailable.map((type) => (
                            <Dropdown.Item
                                key={type}
                                onClick={() => setTypeFilter(type)}
                            >
                                {type}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <InputGroup className="datepicker-container">
                    <DatePicker
                        selected={acadYearFilter ? new Date(acadYearFilter, 0) : null}
                        onChange={(date) => {
                            const year = date ? date.getFullYear() : null;
                            setAcadYearFilter(year);
                        }}
                        placeholderText="ACAD Year"
                        className="filter-datepicker"
                        showYearPicker
                        dateFormat="yyyy"
                    />
                    <CalendarIcon />
                </InputGroup>
                <InputGroup className="datepicker-container">
                    <DatePicker
                        selected={startDateFilter}
                        onChange={(date) => setStartDateFilter(date)}
                        placeholderText="Start date"
                        className="filter-datepicker"
                    />
                    <CalendarIcon />
                </InputGroup>
                <InputGroup className="datepicker-container">
                    <DatePicker
                        selected={endDateFilter}
                        onChange={(date) => setEndDateFilter(date)}
                        placeholderText="End date"
                        className="filter-datepicker"
                    />
                    <CalendarIcon />
                </InputGroup>
            </div>
        </div>
    );
}
